import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  type PreviewSettingsRequest,
  type SettingsPreviewResponse,
  SettingsPreviewResponseSchema,
  type SubscriptionId,
} from '../../api';
import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { newIdempotencyKey } from '../../lib/api/idempotency-key';

import type { RiskPreviewState } from './risk-preview-state';
import { settingsPath } from './settings-api';
import { sameSizing } from './settings-draft';

/** Wait after the last keystroke before asking for the preview (M2 spec §5, proposal). */
export const PREVIEW_DEBOUNCE_MS = 300;

/** The answer to one request: for which request and which attempt (a Retry is a new attempt). */
interface Settled {
  readonly request: PreviewSettingsRequest;
  readonly attempt: number;
  readonly result:
    | { readonly ok: true; readonly preview: SettingsPreviewResponse }
    | { readonly ok: false; readonly error: ApiError };
}

/**
 * The money example of E7 for `request` (M2 R4): `POST …/settings/preview`, debounced, for the
 * current size and fallback SL distance only (P9: the fallback is sent too, null when none).
 * While the answer for the current request is awaited the state is `loading`, so Save is
 * disabled from the first keystroke (AC5).
 *
 * Every change of size or fallback distance starts a new generation: the answer kept from an
 * earlier request is dropped at once (so going A → B → A waits for a fresh answer for A, the
 * capital may have moved), and the replaced request is aborted. An answer from an older
 * generation can never become current.
 *
 * The preview writes nothing, but X3 still applies to a POST: each request, a Retry included,
 * carries a fresh `Idempotency-Key` (M2 R18).
 */
export function useRiskPreview(
  subscriptionId: SubscriptionId,
  request: PreviewSettingsRequest | null,
): { readonly state: RiskPreviewState; readonly retry: () => void } {
  const api = useApiClient();
  // The form builds a new request object on every render: keep one object per request value,
  // so the effect runs again only when the size or the fallback distance really changes.
  const sizing = request?.sizing ?? null;
  const mode = sizing?.mode ?? null;
  const value =
    sizing === null ? null : sizing.mode === 'RISK_PERCENT' ? sizing.riskPercent : sizing.lots;
  const fallback = request?.fallbackStopLossPips ?? null;
  const requested = useMemo<PreviewSettingsRequest | null>(() => {
    if (mode === null || value === null) return null;
    return {
      sizing: mode === 'RISK_PERCENT' ? { mode, riskPercent: value } : { mode, lots: value },
      fallbackStopLossPips: fallback,
    };
  }, [mode, value, fallback]);
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled | null>(null);

  // A new size is a new generation: forget the last answer during render (React's "adjust
  // state when a prop changes" pattern), before any effect could show it as current.
  const [generation, setGeneration] = useState(requested);
  if (generation !== requested) {
    setGeneration(requested);
    setSettled(null);
  }

  useEffect(() => {
    if (requested === null) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .post(
          `${settingsPath(subscriptionId)}/preview`,
          {
            guard: { kind: 'authenticated', idempotencyKey: newIdempotencyKey() },
            body: requested,
          },
          SettingsPreviewResponseSchema,
          controller.signal,
        )
        .then(
          (preview) => {
            if (!controller.signal.aborted) {
              setSettled({ request: requested, attempt, result: { ok: true, preview } });
            }
          },
          (error: unknown) => {
            if (!controller.signal.aborted) {
              setSettled({
                request: requested,
                attempt,
                result: { ok: false, error: toApiError(error) },
              });
            }
          },
        );
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, subscriptionId, requested, attempt]);

  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  return { state: previewState(requested, attempt, settled), retry };
}

function previewState(
  request: PreviewSettingsRequest | null,
  attempt: number,
  settled: Settled | null,
): RiskPreviewState {
  if (request === null) return { status: 'idle' };
  if (
    settled?.attempt !== attempt ||
    !sameSizing(settled.request.sizing, request.sizing) ||
    settled.request.fallbackStopLossPips !== request.fallbackStopLossPips
  ) {
    return { status: 'loading', ...request };
  }
  return settled.result.ok
    ? { status: 'ready', ...request, preview: settled.result.preview }
    : { status: 'failed', ...request, error: settled.result.error };
}
